import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getEvents, API_BASE_URL } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslatedText } from "../../utils/translationHelper";
import "../styles/allEvents.css";
import { isEventActive } from "../../utils/eventDates";

const EVENTS_PER_PAGE = 9;

const translations = {
  kz: {
    pageTitle: "Барлық іс-шаралар",
    searchPlaceholder: "Іс-шараларды іздеу...",
    searchButton: "Іздеу",
    noEvents: "Іс-шаралар табылмады",
    noEventsDesc: "Өзіңізге қажетті іс-шараны табу үшін іздеу сұрауын өзгертіңіз",
    prev: "Артқа",
    next: "Алға",
    loading: "Іс-шаралар жүктелуде...",
    moreDetails: "Толығырақ",
  },
  ru: {
    pageTitle: "Все мероприятия",
    searchPlaceholder: "Поиск мероприятий...",
    searchButton: "Поиск",
    noEvents: "Мероприятия не найдены",
    noEventsDesc: "Попробуйте изменить поисковый запрос",
    prev: "Назад",
    next: "Вперед",
    loading: "Загрузка мероприятий...",
    moreDetails: "Подробнее",
  },
  en: {
    pageTitle: "All Events",
    searchPlaceholder: "Search events...",
    searchButton: "Search",
    noEvents: "No events found",
    noEventsDesc: "Try changing your search query",
    prev: "Previous",
    next: "Next",
    loading: "Loading events...",
    moreDetails: "More Details",
  },
};

function AllEvents() {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const response = await getEvents();
        if (response?.events) {
          setEvents(response.events);
        }
      } catch (error) {
        console.error("Error loading events:", error);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredEvents = useMemo(() => {
    let result = events.filter(event => isEventActive(event));

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((event) => {
        const name = getTranslatedText(event.eventName, currentLanguage).toLowerCase();
        const desc = getTranslatedText(event.eventDescription, currentLanguage).toLowerCase();
        const place = getTranslatedText(event.eventPlace, currentLanguage).toLowerCase();
        return name.includes(q) || desc.includes(q) || place.includes(q);
      });
    }

    return result;
  }, [events, searchQuery, currentLanguage]);

  const totalPages = Math.ceil(filteredEvents.length / EVENTS_PER_PAGE);
  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * EVENTS_PER_PAGE,
    currentPage * EVENTS_PER_PAGE
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleCardClick = (id) => {
    navigate(`/events/${id}`);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatEventPeriod = (event) => {
    if (event.date) {
      const parts = event.date.split("–").map(s => s.trim());
      if (parts.length === 2 && parts[0] === parts[1]) return parts[0];
      return event.date;
    }
    const fmt = (d) => {
      if (!d) return "";
      const [y, m, day] = d.split("-");
      return `${day}.${m}.${y}`;
    };
    if (event.startDate && event.endDate) {
      if (event.startDate === event.endDate) {
        return fmt(event.startDate);
      }
      return `${fmt(event.startDate)} – ${fmt(event.endDate)}`;
    }
    if (event.startDate) return fmt(event.startDate);
    if (event.endDate) return fmt(event.endDate);
    return "";
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (loading) {
    return (
      <div className="all-events-page">
        <div className="all-events-loading">
          <div className="loading-spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="all-events-page">
      <div className="all-events-header">
        <div className="all-events-title-line">
          <span className="all-events-line" />
          <h2 className="all-events-title">{t.pageTitle}</h2>
          <span className="all-events-line" />
        </div>
      </div>

      <div className="all-events-controls">
        <form className="all-events-search" onSubmit={handleSearch}>
          <input
            type="text"
            className="all-events-search-input"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="all-events-search-btn">
            {t.searchButton}
          </button>
        </form>

      </div>

      {paginatedEvents.length > 0 ? (
        <>
          <div className="all-events-grid">
            {paginatedEvents.map((event) => {
              const eventName = getTranslatedText(event.eventName, currentLanguage);
              const description = getTranslatedText(event.eventDescription, currentLanguage);
              const place = getTranslatedText(event.eventPlace, currentLanguage);

              return (
                <div
                  className="all-events-card"
                  key={event.id}
                  onClick={() => handleCardClick(event.id)}
                >
                  {event.img_url && (
                    <div className="all-events-card-image">
                      <img
                        src={API_BASE_URL + event.img_url}
                        alt={eventName}
                      />
                    </div>
                  )}
                  <div className="all-events-card-body">
                    <div className="all-events-card-period">
                      {formatEventPeriod(event)}
                    </div>
                    <h3 className="all-events-card-title">
                      {eventName.length > 60
                        ? eventName.slice(0, 60) + "..."
                        : eventName}
                    </h3>
                    {place && (
                      <div className="all-events-card-place">{place}</div>
                    )}
                    {description && (
                      <p className="all-events-card-desc">
                        {description.length > 120
                          ? description.slice(0, 120) + "..."
                          : description}
                      </p>
                    )}
                    <span className="all-events-card-more">
                      {t.moreDetails} →
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="all-events-pagination">
              <button
                className="all-events-page-btn all-events-page-arrow"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <span className="arrow-icon">←</span>
                <span className="arrow-text">{t.prev}</span>
              </button>

              <div className="all-events-page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`dots-${index}`} className="all-events-page-dots">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`all-events-page-btn all-events-page-num ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageClick(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                className="all-events-page-btn all-events-page-arrow"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <span className="arrow-text">{t.next}</span>
                <span className="arrow-icon">→</span>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="all-events-empty">
          <p>{t.noEvents}</p>
          <span>{t.noEventsDesc}</span>
        </div>
      )}
    </div>
  );
}

export default AllEvents;
